// Core Life Plan types - the central data model for the entire application

import type { FilingStatus } from './budget.types'

// ============================================================================
// PERSONAL INFO
// ============================================================================

export interface PersonalInfo {
  firstName: string
  lastName: string
  birthDate: string              // ISO date
  retirementAge: number
  lifeExpectancy: number
  state: string                  // US state code
  filingStatus: FilingStatus
  email?: string
}

// ============================================================================
// FAMILY
// ============================================================================

export type MaritalStatus = 'single' | 'married' | 'partnered' | 'divorced' | 'widowed'

export interface SpouseInfo {
  firstName: string
  birthDate: string
  retirementAge: number
  currentIncome: number
  industry?: string
  employmentStatus: 'employed' | 'self-employed' | 'unemployed' | 'retired' | 'homemaker'
}

export interface Child {
  id: string
  firstName: string
  birthDate: string
  collegeFundingPlan: 'none' | 'partial' | 'full'
  expectedCollegeCost: number     // Annual cost in today's dollars
  collegeYears: number            // Usually 4
  isDependent: boolean
}

export interface PlannedChild {
  id: string
  plannedYear: number
  collegeFundingPlan: 'none' | 'partial' | 'full'
  expectedCollegeCost: number
}

export interface Dependent {
  id: string
  name: string
  relationship: 'parent' | 'sibling' | 'other'
  monthlySupport: number
  supportEndDate?: string
}

export interface FamilyInfo {
  maritalStatus: MaritalStatus
  spouse?: SpouseInfo
  children: Child[]
  plannedChildren: PlannedChild[]
  dependents: Dependent[]
}

// ============================================================================
// CAREER
// ============================================================================

export type EmploymentStatus = 'employed' | 'self-employed' | 'unemployed' | 'student' | 'retired'

export type EducationLevel = 
  | 'high_school'
  | 'some_college'
  | 'associates'
  | 'bachelors'
  | 'masters'
  | 'doctorate'
  | 'professional'  // MD, JD, etc.

export interface JobInfo {
  title: string
  employer: string
  industry: string
  yearsInRole: number
  yearsExperience: number
  baseSalary: number
  bonus: number                   // Annual expected bonus
  commission: number              // Annual expected commission
  stockCompensation: number       // Annual vesting value
  employerMatch401k: number       // Percentage matched
  employerMatchLimit: number      // Max employer contribution
}

export interface SideIncome {
  id: string
  name: string
  annualAmount: number
  isRecurring: boolean
  endDate?: string
}

export interface PlannedCareerChange {
  id: string
  type: 'promotion' | 'job_change' | 'industry_change' | 'sabbatical' | 'parental_leave' | 'retirement'
  targetDate: string
  salaryChange: number            // New salary or delta
  description: string
}

export interface CareerInfo {
  employmentStatus: EmploymentStatus
  educationLevel: EducationLevel
  currentJob?: JobInfo
  sideIncomes: SideIncome[]
  plannedChanges: PlannedCareerChange[]
}

// ============================================================================
// ASSETS
// ============================================================================

export type AssetCategory = 
  | 'cash'
  | 'checking'
  | 'savings'
  | 'money_market'
  | 'cd'
  | 'brokerage'
  | '401k'
  | '403b'
  | 'ira_traditional'
  | 'ira_roth'
  | 'hsa'
  | '529'
  | 'pension'
  | 'real_estate'
  | 'vehicle'
  | 'business'
  | 'crypto'
  | 'collectibles'
  | 'other'

export interface Asset {
  id: string
  name: string
  category: AssetCategory
  currentValue: number
  expectedReturn: number          // Annual % return
  contributions: number           // Annual contributions
  isRetirementAccount: boolean
  taxAdvantaged: boolean
  notes?: string
}

// ============================================================================
// DEBTS
// ============================================================================

export type DebtCategory = 
  | 'mortgage'
  | 'home_equity'
  | 'auto'
  | 'student_federal'
  | 'student_private'
  | 'credit_card'
  | 'personal_loan'
  | 'medical'
  | 'business'
  | 'other'

export interface LifePlanDebt {
  id: string
  name: string
  category: DebtCategory
  balance: number
  interestRate: number
  minimumPayment: number
  extraPayment: number
  payoffDate?: string             // Calculated or target
  notes?: string
}

// ============================================================================
// INSURANCE
// ============================================================================

export interface HealthInsurance {
  type: 'employer' | 'marketplace' | 'medicare' | 'medicaid' | 'none'
  monthlyPremium: number
  deductible: number
  outOfPocketMax: number
  hsaEligible: boolean
}

export interface LifeInsurance {
  id: string
  type: 'term' | 'whole' | 'universal'
  coverageAmount: number
  monthlyPremium: number
  termLength?: number             // Years for term policies
  cashValue?: number              // For whole/universal
}

export interface DisabilityInsurance {
  hasShortTerm: boolean
  shortTermCoverage: number       // % of income
  shortTermDuration: number       // Months
  hasLongTerm: boolean
  longTermCoverage: number        // % of income
  longTermWaitingPeriod: number   // Days before benefits start
  monthlyPremium: number
}

export interface InsuranceInfo {
  health: HealthInsurance
  life: LifeInsurance[]
  disability: DisabilityInsurance
  autoAnnual: number
  homeAnnual: number
  rentersAnnual: number
  umbrellaAnnual: number
  longTermCareAnnual: number
}

// ============================================================================
// GOALS (Timeline-integrated)
// ============================================================================

export type GoalCategory = 
  | 'emergency_fund'
  | 'retirement'
  | 'home_purchase'
  | 'home_renovation'
  | 'vehicle'
  | 'education'
  | 'wedding'
  | 'vacation'
  | 'debt_payoff'
  | 'investment'
  | 'charitable'
  | 'business'
  | 'other'

export interface TimelineGoal {
  id: string
  name: string
  category: GoalCategory
  targetAmount: number
  currentAmount: number
  targetDate: string
  priority: 'low' | 'medium' | 'high' | 'critical'
  monthlyContribution: number
  linkedAssetId?: string          // Can link to specific savings account
  notes?: string
}

// ============================================================================
// PREFERENCES
// ============================================================================

export type RiskTolerance = 'conservative' | 'moderate' | 'aggressive'

export interface Preferences {
  inflationRate: number           // Default 2.5%
  riskTolerance: RiskTolerance
  showRealDollars: boolean        // Always show in today's purchasing power
  socialSecurityAge: number       // When they plan to claim
  expectedSocialSecurity: number  // Monthly benefit in today's dollars
  hasPension: boolean
  pensionMonthly: number
  pensionStartAge: number
  expectedInheritance: number
  inheritanceYear?: number
}

// ============================================================================
// MASTER LIFE PLAN STATE
// ============================================================================

export interface LifePlanState {
  // Core sections
  personal: PersonalInfo
  family: FamilyInfo
  career: CareerInfo
  assets: Asset[]
  debts: LifePlanDebt[]
  insurance: InsuranceInfo
  goals: TimelineGoal[]
  preferences: Preferences
  
  // Metadata
  createdAt: string
  updatedAt: string
  questionnaireCompleted: boolean
  questionnaireProgress: number   // 0-100
  
  // Computed (cached for performance)
  computed: {
    totalAssets: number
    totalDebts: number
    netWorth: number
    monthlyIncome: number
    monthlyExpenses: number
    savingsRate: number
    yearsToRetirement: number
  }
}

// ============================================================================
// INITIAL STATE FACTORY
// ============================================================================

export const createInitialLifePlan = (): LifePlanState => ({
  personal: {
    firstName: '',
    lastName: '',
    birthDate: '',
    retirementAge: 65,
    lifeExpectancy: 90,
    state: '',
    filingStatus: 'single',
  },
  family: {
    maritalStatus: 'single',
    children: [],
    plannedChildren: [],
    dependents: [],
  },
  career: {
    employmentStatus: 'employed',
    educationLevel: 'bachelors',
    sideIncomes: [],
    plannedChanges: [],
  },
  assets: [],
  debts: [],
  insurance: {
    health: {
      type: 'employer',
      monthlyPremium: 0,
      deductible: 0,
      outOfPocketMax: 0,
      hsaEligible: false,
    },
    life: [],
    disability: {
      hasShortTerm: false,
      shortTermCoverage: 0,
      shortTermDuration: 0,
      hasLongTerm: false,
      longTermCoverage: 0,
      longTermWaitingPeriod: 0,
      monthlyPremium: 0,
    },
    autoAnnual: 0,
    homeAnnual: 0,
    rentersAnnual: 0,
    umbrellaAnnual: 0,
    longTermCareAnnual: 0,
  },
  goals: [],
  preferences: {
    inflationRate: 2.5,
    riskTolerance: 'moderate',
    showRealDollars: true,
    socialSecurityAge: 67,
    expectedSocialSecurity: 0,
    hasPension: false,
    pensionMonthly: 0,
    pensionStartAge: 65,
    expectedInheritance: 0,
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  questionnaireCompleted: false,
  questionnaireProgress: 0,
  computed: {
    totalAssets: 0,
    totalDebts: 0,
    netWorth: 0,
    monthlyIncome: 0,
    monthlyExpenses: 0,
    savingsRate: 0,
    yearsToRetirement: 0,
  },
})

// ============================================================================
// INDUSTRY LIST (for AI wage prediction)
// ============================================================================

export const INDUSTRIES = [
  'Technology - Software',
  'Technology - Hardware',
  'Technology - AI/ML',
  'Technology - Cybersecurity',
  'Healthcare - Physician',
  'Healthcare - Nursing',
  'Healthcare - Administration',
  'Healthcare - Pharmaceutical',
  'Finance - Investment Banking',
  'Finance - Asset Management',
  'Finance - Commercial Banking',
  'Finance - Insurance',
  'Finance - Fintech',
  'Legal - Law Firm',
  'Legal - Corporate',
  'Legal - Government',
  'Consulting - Management',
  'Consulting - IT',
  'Consulting - Strategy',
  'Manufacturing - Aerospace',
  'Manufacturing - Automotive',
  'Manufacturing - Consumer Goods',
  'Retail - E-commerce',
  'Retail - Brick and Mortar',
  'Real Estate - Commercial',
  'Real Estate - Residential',
  'Education - K-12',
  'Education - Higher Ed',
  'Education - EdTech',
  'Government - Federal',
  'Government - State/Local',
  'Government - Military',
  'Nonprofit - Healthcare',
  'Nonprofit - Education',
  'Nonprofit - Other',
  'Energy - Oil & Gas',
  'Energy - Renewable',
  'Energy - Utilities',
  'Media - Entertainment',
  'Media - Publishing',
  'Media - Advertising',
  'Hospitality - Hotels',
  'Hospitality - Restaurants',
  'Transportation - Airlines',
  'Transportation - Logistics',
  'Construction',
  'Agriculture',
  'Telecommunications',
  'Other',
] as const

export type Industry = typeof INDUSTRIES[number]
